import { Brain, User } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface CopilotMessageProps {
  role: 'user' | 'assistant';
  content: string;
}

const chatProseClasses = `
  prose prose-sm max-w-none
  prose-headings:text-[hsl(var(--sg7-gold))] prose-headings:font-bold prose-headings:mt-3 prose-headings:mb-2
  prose-h3:text-sm prose-h3:mt-2
  prose-p:text-[hsl(var(--sg7-text))] prose-p:text-sm prose-p:leading-relaxed prose-p:my-2
  prose-strong:text-[hsl(var(--sg7-text))] prose-strong:font-semibold
  prose-ul:my-2 prose-ul:pl-4
  prose-ol:my-2 prose-ol:pl-4
  prose-li:text-[hsl(var(--sg7-text))] prose-li:text-sm prose-li:my-1
  prose-li:marker:text-[hsl(var(--sg7-gold))]
`;

export function CopilotMessage({ role, content }: CopilotMessageProps) {
  const isAssistant = role === 'assistant';

  return (
    <div className={cn(
      "flex gap-3 p-3 rounded-lg",
      isAssistant 
        ? "bg-[hsl(var(--sg7-surface-2))]" 
        : "bg-muted/30"
    )}>
      {/* Avatar */}
      <div className={cn(
        "shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
        isAssistant 
          ? "bg-gradient-to-br from-[hsl(var(--sg7-gold))] to-[hsl(var(--sg7-gold-dark))]" 
          : "bg-muted"
      )}>
        {isAssistant ? (
          <Brain className="h-4 w-4 text-[hsl(var(--sg7-surface-1))]" />
        ) : (
          <User className="h-4 w-4 text-[hsl(var(--sg7-text-secondary))]" />
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm font-medium mb-1",
          isAssistant ? "text-[hsl(var(--sg7-gold))]" : "text-[hsl(var(--sg7-text))]"
        )}>
          {isAssistant ? "SG7 Copilot" : "You"}
        </p>
        <div className="text-sm text-[hsl(var(--sg7-text))]">
          {content ? (
            isAssistant ? (
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]} 
                className={chatProseClasses}
              >
                {content}
              </ReactMarkdown>
            ) : (
              <span className="whitespace-pre-wrap break-words">{content}</span>
            )
          ) : (
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-[hsl(var(--sg7-gold))] rounded-full animate-pulse" />
              <span className="w-2 h-2 bg-[hsl(var(--sg7-gold))] rounded-full animate-pulse delay-100" />
              <span className="w-2 h-2 bg-[hsl(var(--sg7-gold))] rounded-full animate-pulse delay-200" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
